// src/pages/Users/Inscription/EditDocumentsModal.jsx

import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  FaCheckCircle,
  FaFileMedical,
  FaFilePdf,
  FaGoogleDrive,
  FaImage,
  FaLink,
  FaPlusCircle,
  FaSave,
  FaTimes,
  FaTimesCircle,
  FaTrashAlt,
} from "react-icons/fa";

import Button from "../../../components/Button/Button";
import {
  deleteDossierFile,
  updateDossierInscription,
  uploadCertificatMedical,
  uploadLicenceDefinitive,
} from "../../../services/dossierServices";

/* ---------------- Helpers ---------------- */

const SERVER_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/+$/, "");

const toAbsoluteUrl = (url) => {
  if (!url) return "";

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const cleanUrl = url.startsWith("/") ? url : `/${url}`;

  return `${SERVER_BASE}${cleanUrl}`;
};

const isDrive = (url) =>
  !!url &&
  /https?:\/\/(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(
    url,
  );

const isImage = (url) =>
  !!url && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split("?")[0]);

const isPdf = (url) =>
  !!url &&
  (url.toLowerCase().endsWith(".pdf") ||
    url.toLowerCase().includes("pdf"));

const toInputDateSafe = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);

    if (match) {
      return match[1];
    }
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* ---------------- Bouton document ---------------- */

const BigDocButton = ({ url, onClick, emptyLabel = "Aucun document" }) => {
  const hasDocument = Boolean(url);

  let Icon = FaTimesCircle;
  let color = "text-slate-300";
  let label = emptyLabel;

  if (hasDocument) {
    if (isDrive(url)) {
      Icon = FaGoogleDrive;
      color = "text-sky-600";
      label = "Lien Google Drive";
    } else if (isPdf(url)) {
      Icon = FaFilePdf;
      color = "text-violet-600";
      label = "Fichier PDF";
    } else if (isImage(url)) {
      Icon = FaImage;
      color = "text-emerald-600";
      label = "Image";
    } else {
      Icon = FaLink;
      color = "text-slate-600";
      label = "Lien externe";
    }
  }

  return (
    <button
      type="button"
      onClick={hasDocument ? onClick : undefined}
      disabled={!hasDocument}
      className={`group flex w-full flex-col items-center justify-center rounded-2xl border border-dashed p-4 transition sm:p-5 ${
        hasDocument
          ? "border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          : "cursor-not-allowed border-slate-300 bg-slate-50/50 dark:bg-slate-900/30"
      }`}
      title={hasDocument ? "Ouvrir le document" : emptyLabel}
    >
      <Icon
        className={`mb-1.5 text-4xl transition sm:mb-2 sm:text-5xl ${color} ${
          hasDocument ? "group-hover:scale-105" : ""
        }`}
      />

      <div
        className={`text-xs sm:text-sm ${
          hasDocument
            ? "text-slate-800 dark:text-slate-100"
            : "text-slate-400"
        }`}
      >
        {label}
      </div>

      {hasDocument && (
        <div className="mt-0.5 text-[11px] text-slate-500 sm:mt-1 sm:text-xs">
          Cliquer pour ouvrir
        </div>
      )}
    </button>
  );
};

/* ---------------- Section ---------------- */

const SectionCard = ({ title, icon, children }) => (
  <div className="rounded-2xl bg-white/90 p-4 ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
    <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
      <span className="text-base sm:text-lg">{icon}</span>

      <h4 className="text-sm font-semibold sm:text-base">{title}</h4>
    </div>

    <div className="mt-3">{children}</div>
  </div>
);

/* ---------------- Formulaire fichier ou lien ---------------- */

const FormUploadOrLink = ({
  mode,
  setMode,
  setFile,
  url,
  setUrl,
  onSave,
  onCancel,
  saving,
  currentDocument,
  saveLabel = "Enregistrer",
}) => {
  const handleModeChange = (newMode) => {
    setMode(newMode);

    if (newMode === "upload") {
      setFile(null);
      return;
    }

    setFile(null);

    if (currentDocument?.startsWith("/uploads/")) {
      setUrl("");
    } else {
      setUrl(currentDocument || "");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-[13px] sm:text-sm">
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            value="upload"
            checked={mode === "upload"}
            onChange={() => handleModeChange("upload")}
            disabled={saving}
          />

          Téléverser un fichier
        </label>

        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            value="link"
            checked={mode === "link"}
            onChange={() => handleModeChange("link")}
            disabled={saving}
          />

          Coller un lien
        </label>
      </div>

      {mode === "upload" ? (
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={(event) =>
            setFile(event.target.files?.[0] || null)
          }
          disabled={saving}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      ) : (
        <input
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://drive.google.com/..."
          disabled={saving}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          text={saving ? "Enregistrement…" : saveLabel}
          icon={FaSave}
          onClick={onSave}
          disabled={saving}
          className="!rounded-lg !px-2.5 !py-1.5 !text-xs"
        />

        <Button
          type="button"
          text="Annuler"
          icon={FaTimes}
          onClick={onCancel}
          disabled={saving}
          className="!rounded-lg !px-2.5 !py-1.5 !text-xs"
        />
      </div>
    </div>
  );
};

/* ---------------- Modale principale ---------------- */

const EditDocumentsModal = ({
  isOpen,
  onClose,
  dossier,
  refreshDossiers,
}) => {
  const dateInputRef = useRef(null);

  const [saving, setSaving] = useState(false);

  /* Affichage des formulaires */
  const [showFormCM, setShowFormCM] = useState(false);
  const [showFormLicence, setShowFormLicence] = useState(false);

  /* Document médical */
  const [modeCM, setModeCM] = useState("upload");
  const [fileCM, setFileCM] = useState(null);
  const [urlCM, setUrlCM] = useState("");
  const [dateCM, setDateCM] = useState("");

  /* Licence-assurance finale */
  const [modeLicence, setModeLicence] = useState("upload");
  const [fileLicence, setFileLicence] = useState(null);
  const [urlLicence, setUrlLicence] = useState("");

  /* Valeurs présentes dans le dossier */
  const currentCM = dossier?.cm_url || "";

  const currentLicence =
    dossier?.licence_url ||
    dossier?.licence_assurance_url ||
    dossier?.licence_assurance ||
    "";

  /* ---------------- Initialisation ---------------- */

  useEffect(() => {
    if (!isOpen || !dossier?.id) return;

    setShowFormCM(false);
    setShowFormLicence(false);

    setModeCM(
      currentCM && currentCM.startsWith("/uploads/")
        ? "upload"
        : currentCM
          ? "link"
          : "upload",
    );

    setFileCM(null);
    setUrlCM(currentCM);
    setDateCM(toInputDateSafe(dossier?.date_cm));

    setModeLicence(
      currentLicence && currentLicence.startsWith("/uploads/")
        ? "upload"
        : currentLicence
          ? "link"
          : "upload",
    );

    setFileLicence(null);
    setUrlLicence(currentLicence);

    setSaving(false);
  }, [
    isOpen,
    dossier?.id,
    dossier?.date_cm,
    currentCM,
    currentLicence,
  ]);

  if (!isOpen || !dossier?.id) {
    return null;
  }

  /* ---------------- Actions communes ---------------- */

  const openUrl = (url) => {
    if (!url) return;

    window.open(toAbsoluteUrl(url), "_blank", "noopener,noreferrer");
  };

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) {
      return;
    }

    try {
      setSaving(true);

      await deleteDossierFile(dossier.id, field);
      await refreshDossiers?.();

      toast.success(`${label} supprimé.`);
    } catch (error) {
      console.error(error);
      toast.error(`Échec de la suppression de « ${label} ».`);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Document médical ---------------- */

  const saveCM = async () => {
    if (modeCM === "upload" && !fileCM) {
      toast.error("Choisissez un fichier PDF ou une image.");
      return;
    }

    const cleanUrl = urlCM.trim();

    if (modeCM === "link" && !cleanUrl) {
      toast.error("Indiquez le lien du document médical.");
      return;
    }

    if (modeCM === "link" && !/^https?:\/\//i.test(cleanUrl)) {
      toast.error("Le lien indiqué n’est pas valide.");
      return;
    }

    try {
      setSaving(true);

      if (modeCM === "upload") {
        await uploadCertificatMedical(
          dossier.id,
          fileCM,
          dateCM
            ? {
                date_cm: dateCM,
              }
            : undefined,
        );
      } else {
        if (currentCM.startsWith("/uploads/")) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch (error) {
            console.error(
              "Impossible de supprimer l’ancien document médical :",
              error,
            );
          }
        }

        await updateDossierInscription(dossier.id, {
          cm_url: cleanUrl,
          ...(dateCM ? { date_cm: dateCM } : {}),
        });
      }

      await refreshDossiers?.();

      setFileCM(null);
      setShowFormCM(false);

      toast.success("Document médical mis à jour.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement du document médical.");
    } finally {
      setSaving(false);
    }
  };

  const saveCMDate = async () => {
    if (!dateCM) {
      toast.error("Indiquez la date du certificat ou de l’attestation.");
      dateInputRef.current?.focus();
      return;
    }

    try {
      setSaving(true);

      await updateDossierInscription(dossier.id, {
        date_cm: dateCM,
      });

      await refreshDossiers?.();

      toast.success("Date du document médical enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement de la date.");
    } finally {
      setSaving(false);
    }
  };

  const removeMedicalDocument = async () => {
    await removeField("cm_url", "Document médical");

    setFileCM(null);
    setUrlCM("");
    setShowFormCM(false);
  };

  /* ---------------- Licence-assurance finale ---------------- */

  const saveLicence = async () => {
    if (modeLicence === "upload" && !fileLicence) {
      toast.error("Choisissez un fichier PDF ou une image.");
      return;
    }

    const cleanUrl = urlLicence.trim();

    if (modeLicence === "link" && !cleanUrl) {
      toast.error("Indiquez le lien de la licence-assurance.");
      return;
    }

    if (modeLicence === "link" && !/^https?:\/\//i.test(cleanUrl)) {
      toast.error("Le lien indiqué n’est pas valide.");
      return;
    }

    try {
      setSaving(true);

      if (modeLicence === "upload") {
        await uploadLicenceDefinitive(dossier.id, fileLicence);
      } else {
        /*
         * Suppression de l’ancien fichier local avant son remplacement
         * par un lien externe.
         */
        if (currentLicence.startsWith("/uploads/")) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch (error) {
            console.error(
              "Impossible de supprimer l’ancienne licence-assurance :",
              error,
            );
          }
        }

        await updateDossierInscription(dossier.id, {
          licence_url: cleanUrl,
          licence_assurance_url: cleanUrl,
          licence_assurance: cleanUrl,
        });
      }

      await refreshDossiers?.();

      setFileLicence(null);
      setShowFormLicence(false);

      toast.success("Licence-assurance finale mise à jour.");
    } catch (error) {
      console.error(error);
      toast.error(
        "Erreur lors de l’enregistrement de la licence-assurance.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeLicenceDocument = async () => {
    await removeField("licence_url", "Licence-assurance finale");

    setFileLicence(null);
    setUrlLicence("");
    setShowFormLicence(false);
  };

  /* ---------------- Classes des boutons ---------------- */

  const btnSmall = "!rounded-lg !px-2.5 !py-1.5 !text-xs";

  const btnDangerSmall =
    "!rounded-lg !bg-rose-600 !px-2.5 !py-1.5 !text-xs hover:!bg-rose-700";

  /* ---------------- Rendu ---------------- */

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="documents-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose?.();
        }
      }}
    >
      <div
        className="relative flex max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white/95 shadow-2xl ring-1 ring-slate-200/60 dark:bg-slate-900/95 dark:ring-slate-800/60"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6">
          <div>
            <h3
              id="documents-modal-title"
              className="text-lg font-bold tracking-tight text-white sm:text-xl"
            >
              Documents d’inscription
            </h3>

            <p className="text-[11px] text-white/90 sm:text-xs">
              Gérez le document médical et la licence-assurance finale.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!saving) {
                onClose?.();
              }
            }}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/15 text-white transition hover:bg-white/25 disabled:opacity-50 sm:h-9 sm:w-9"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Contenu */}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 sm:p-6">
          {/* Document médical */}
          <SectionCard
            title="Certificat ou attestation médicale"
            icon={<FaFileMedical />}
          >
            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto]">
              <BigDocButton
                url={currentCM}
                onClick={() => openUrl(currentCM)}
                emptyLabel="Aucun document médical"
              />

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentCM ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormCM((current) => !current)}
                  disabled={saving}
                  className={`${btnSmall} gap-1.5`}
                />

                {currentCM && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={removeMedicalDocument}
                    disabled={saving}
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {/* Date médicale */}
            <div className="mt-4 grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_auto]">
              <div>
                <label
                  htmlFor={`medical-document-date-${dossier.id}`}
                  className="mb-1 block text-[13px] font-medium"
                >
                  Date figurant sur le document
                </label>

                <input
                  ref={dateInputRef}
                  id={`medical-document-date-${dossier.id}`}
                  type="date"
                  value={dateCM}
                  onChange={(event) => setDateCM(event.target.value)}
                  disabled={saving}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                />

                <p className="mt-1 text-xs text-slate-500">
                  La date peut être enregistrée indépendamment du document.
                </p>
              </div>

              <Button
                type="button"
                text={saving ? "Enregistrement…" : "Enregistrer la date"}
                icon={FaSave}
                onClick={saveCMDate}
                disabled={saving}
                className={`${btnSmall} gap-2`}
              />
            </div>

            {showFormCM && (
              <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
                <FormUploadOrLink
                  mode={modeCM}
                  setMode={setModeCM}
                  setFile={setFileCM}
                  url={urlCM}
                  setUrl={setUrlCM}
                  onSave={saveCM}
                  onCancel={() => {
                    setShowFormCM(false);
                    setFileCM(null);
                    setUrlCM(currentCM);
                  }}
                  saving={saving}
                  currentDocument={currentCM}
                  saveLabel="Enregistrer le document"
                />
              </div>
            )}
          </SectionCard>

          {/* Licence-assurance finale */}
          <SectionCard
            title="Licence-assurance finale"
            icon={<FaCheckCircle />}
          >
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
              Ce document correspond à la licence-assurance définitive. Il ne
              s’agit pas du formulaire de demande de licence à signer.
            </p>

            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto]">
              <BigDocButton
                url={currentLicence}
                onClick={() => openUrl(currentLicence)}
                emptyLabel="Aucune licence-assurance"
              />

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentLicence ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() =>
                    setShowFormLicence((current) => !current)
                  }
                  disabled={saving}
                  className={`${btnSmall} gap-1.5`}
                />

                {currentLicence && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={removeLicenceDocument}
                    disabled={saving}
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {showFormLicence && (
              <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
                <FormUploadOrLink
                  mode={modeLicence}
                  setMode={setModeLicence}
                  setFile={setFileLicence}
                  url={urlLicence}
                  setUrl={setUrlLicence}
                  onSave={saveLicence}
                  onCancel={() => {
                    setShowFormLicence(false);
                    setFileLicence(null);
                    setUrlLicence(currentLicence);
                  }}
                  saving={saving}
                  currentDocument={currentLicence}
                  saveLabel="Enregistrer la licence"
                />
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EditDocumentsModal;